import type {
  FormBusinessDetail,
  FormBusinessDetails,
  FormBranding,
  FormPublicSettings,
  PublicFormFont,
  PublicFormHeaderStyle,
  PublicFormQuestionStyle,
} from '@/types/formsEngine';

/** Server limits for `publicSettings` text fields. */
export const PUBLIC_SETTINGS_LIMITS = {
  consentText: 2000,
  submitLabel: 40,
  successTitle: 120,
  successMessage: 2000,
  redirectUrl: 2000,
} as const;

/** The server's wording for fields it fills in when the template leaves them empty. */
export const PUBLIC_FORM_DEFAULTS = {
  submitLabel: 'Submit',
  successTitle: 'Thanks — we got it',
  successMessage: 'Someone will get in touch soon.',
  consentError: 'Please accept the terms to submit this form.',
} as const;

export type AfterSubmitMode = 'MESSAGE' | 'REDIRECT';

/** The company details a form can show, in the order the builder lists them. */
export const BUSINESS_DETAIL_OPTIONS: ReadonlyArray<{ key: FormBusinessDetail; label: string }> = [
  { key: 'logo', label: 'Logo' },
  { key: 'name', label: 'Business name' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'address', label: 'Address' },
];

/** The header a form showed before details were picked one by one. */
const CLASSIC_BUSINESS_DETAILS: FormBusinessDetails = {
  logo: true,
  name: true,
  email: true,
  phone: true,
  address: false,
};

const HIDDEN_BUSINESS_DETAILS: FormBusinessDetails = {
  logo: false,
  name: false,
  email: false,
  phone: false,
  address: false,
};

/**
 * Fonts a form may use, with the stack each maps to. Inter and Geist ship
 * with the app (index.css loads both); System and Serif are OS stacks, so a
 * public page makes no third-party font request.
 */
export const PUBLIC_FORM_FONTS: ReadonlyArray<{
  value: PublicFormFont;
  label: string;
  family: string;
}> = [
  { value: 'inter', label: 'Inter', family: "'Inter', system-ui, sans-serif" },
  { value: 'geist', label: 'Geist', family: "'Geist Variable', system-ui, sans-serif" },
  {
    value: 'system',
    label: 'System',
    family: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
  },
  { value: 'serif', label: 'Serif', family: "Georgia, 'Times New Roman', serif" },
];

const DEFAULT_FONT: PublicFormFont = 'inter';
const HEADER_STYLES: readonly PublicFormHeaderStyle[] = ['gradient', 'solid', 'minimal'];
const QUESTION_STYLES: readonly PublicFormQuestionStyle[] = ['cards', 'classic'];

/** A value from a fixed list, or the fallback for anything else. */
function choiceOr<T extends string>(choices: readonly T[], value: unknown, fallback: T): T {
  return choices.includes(value as T) ? (value as T) : fallback;
}

/** Every branding field read the way the server reads it, defaults filled in. */
function readBranding(raw: Partial<Record<keyof FormBranding, unknown>> | null | undefined) {
  return {
    primaryColor: normalizeHexColor(raw?.primaryColor),
    font: choiceOr(
      PUBLIC_FORM_FONTS.map((option) => option.value),
      raw?.font,
      DEFAULT_FONT,
    ),
    headerStyle: choiceOr(HEADER_STYLES, raw?.headerStyle, 'gradient'),
    backgroundColor: normalizeHexColor(raw?.backgroundColor),
    questionStyle: choiceOr(QUESTION_STYLES, raw?.questionStyle, 'cards'),
  } satisfies Required<FormBranding>;
}

/** The font-family stack for a form font; anything unknown is Inter. */
export function publicFormFontFamily(font: PublicFormFont | null | undefined): string {
  return (
    PUBLIC_FORM_FONTS.find((option) => option.value === font) ??
    PUBLIC_FORM_FONTS.find((option) => option.value === DEFAULT_FONT)!
  ).family;
}

/** Page backgrounds offered as swatches. `null` is a soft tint of the brand colour. */
export const PUBLIC_FORM_BACKGROUNDS: ReadonlyArray<{ value: string | null; label: string }> = [
  { value: null, label: 'Brand tint' },
  { value: '#f3f4f6', label: 'Light grey' },
  { value: '#faf6ee', label: 'Warm cream' },
  { value: '#ffffff', label: 'White' },
  { value: '#0f172a', label: 'Dark slate' },
];

/** A page background by its swatch name, or its hex when it is a custom colour. */
export function publicFormBackgroundLabel(color: string | null): string {
  return PUBLIC_FORM_BACKGROUNDS.find((option) => option.value === color)?.label ?? color!;
}

/** Strict `#RRGGBB`, matching the server. */
export const HEX_COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/;

/** A valid hex colour, lowercased; anything else is null. */
export function normalizeHexColor(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return HEX_COLOR_PATTERN.test(trimmed) ? trimmed.toLowerCase() : null;
}

/**
 * The editable form of `FormPublicSettings`: every text field is a string (so
 * inputs stay controlled) and the after-submit choice is explicit, so a
 * half-typed redirect URL survives switching back to "Show a message".
 */
export interface PublicSettingsDraft {
  businessDetails: FormBusinessDetails;
  /** Lowercase `#rrggbb`, or null to use the company colour. */
  primaryColor: string | null;
  font: PublicFormFont;
  headerStyle: PublicFormHeaderStyle;
  /** Lowercase `#rrggbb`, or null for a soft tint of the brand colour. */
  backgroundColor: string | null;
  questionStyle: PublicFormQuestionStyle;
  consentText: string;
  submitLabel: string;
  afterSubmit: AfterSubmitMode;
  successTitle: string;
  successMessage: string;
  redirectUrl: string;
}

export function defaultPublicSettingsDraft(): PublicSettingsDraft {
  return {
    businessDetails: { ...CLASSIC_BUSINESS_DETAILS },
    primaryColor: null,
    font: DEFAULT_FONT,
    headerStyle: 'gradient',
    backgroundColor: null,
    questionStyle: 'cards',
    consentText: '',
    submitLabel: '',
    afterSubmit: 'MESSAGE',
    successTitle: '',
    successMessage: '',
    redirectUrl: '',
  };
}

export function publicSettingsDraftFrom(
  settings: Partial<FormPublicSettings> | null | undefined,
): PublicSettingsDraft {
  const defaults = defaultPublicSettingsDraft();
  if (!settings) return defaults;
  // A form saved before the details were picked one by one has only the
  // single switch: on is the classic header, off is nothing.
  const legacyDetails =
    settings.showBusinessInfo === false ? HIDDEN_BUSINESS_DETAILS : CLASSIC_BUSINESS_DETAILS;
  return {
    businessDetails: { ...legacyDetails, ...settings.businessDetails },
    ...readBranding(settings.branding),
    consentText: settings.consentText ?? '',
    submitLabel: settings.submitLabel ?? '',
    afterSubmit: settings.redirectUrl ? 'REDIRECT' : 'MESSAGE',
    successTitle: settings.successTitle ?? '',
    successMessage: settings.successMessage ?? '',
    redirectUrl: settings.redirectUrl ?? '',
  };
}

function textOrNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

/**
 * The payload shape. The success title and message are sent in both modes:
 * an embedded page whose redirect the browser blocks falls back to them.
 */
export function buildPublicSettings(
  draft: PublicSettingsDraft,
): FormPublicSettings & Required<Pick<FormPublicSettings, 'businessDetails' | 'branding'>> {
  return {
    showBusinessInfo: Object.values(draft.businessDetails).some(Boolean),
    businessDetails: { ...draft.businessDetails },
    branding: readBranding(draft),
    consentText: textOrNull(draft.consentText),
    submitLabel: textOrNull(draft.submitLabel),
    successTitle: textOrNull(draft.successTitle),
    successMessage: textOrNull(draft.successMessage),
    redirectUrl: draft.afterSubmit === 'REDIRECT' ? textOrNull(draft.redirectUrl) : null,
  };
}

const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1']);

/**
 * Mirrors the server rule: an absolute https URL, or http only for
 * localhost / 127.0.0.1. Anything else (relative, `javascript:`, plain http)
 * is refused.
 */
export function isAllowedRedirectUrl(value: string): boolean {
  let url: URL;
  try {
    url = new URL(value.trim());
  } catch {
    return false;
  }
  if (url.protocol === 'https:') return Boolean(url.hostname);
  return url.protocol === 'http:' && LOCAL_HOSTS.has(url.hostname);
}

/** Inline error for the redirect URL field, or null when it is acceptable. */
export function redirectUrlIssue(draft: PublicSettingsDraft): string | null {
  if (draft.afterSubmit !== 'REDIRECT') return null;
  const url = draft.redirectUrl.trim();
  if (!url) return 'Enter the page to send customers to.';
  if (url.length > PUBLIC_SETTINGS_LIMITS.redirectUrl) {
    return `Use an address of ${PUBLIC_SETTINGS_LIMITS.redirectUrl} characters or fewer.`;
  }
  if (!isAllowedRedirectUrl(url)) {
    return 'Use a full https:// address, like https://example.com/thanks.';
  }
  return null;
}

/** Every reason the public settings cannot be saved, worded for the builder's issue list. */
export function validatePublicSettings(draft: PublicSettingsDraft): string[] {
  const issues: string[] = [];
  const tooLong = (value: string, limit: number, label: string) => {
    if (value.trim().length > limit) issues.push(`${label} must be ${limit} characters or fewer.`);
  };
  tooLong(draft.consentText, PUBLIC_SETTINGS_LIMITS.consentText, 'Consent text');
  tooLong(draft.submitLabel, PUBLIC_SETTINGS_LIMITS.submitLabel, 'Submit button label');
  tooLong(draft.successTitle, PUBLIC_SETTINGS_LIMITS.successTitle, 'Success title');
  tooLong(draft.successMessage, PUBLIC_SETTINGS_LIMITS.successMessage, 'Success message');
  const redirect = redirectUrlIssue(draft);
  if (redirect) issues.push(`Redirect URL: ${redirect}`);
  return issues;
}

function storedText(value: unknown): string | null {
  return typeof value === 'string' ? textOrNull(value) : null;
}

/**
 * A stored version's `publicSettings` (raw, possibly null or partial for a
 * version saved before settings existed) read the way the server reads it.
 */
export function readStoredPublicSettings(raw: unknown): FormPublicSettings {
  const stored =
    raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  return {
    showBusinessInfo: typeof stored.showBusinessInfo === 'boolean' ? stored.showBusinessInfo : true,
    consentText: storedText(stored.consentText),
    submitLabel: storedText(stored.submitLabel),
    successTitle: storedText(stored.successTitle),
    successMessage: storedText(stored.successMessage),
    redirectUrl: storedText(stored.redirectUrl),
    // Kept (checked the way the server reads them) so comparing two versions
    // sees branding and per-detail visibility; absent on older versions.
    ...storedBusinessDetails(stored.businessDetails),
    ...storedBranding(stored.branding),
  };
}

const BUSINESS_DETAIL_KEYS = ['logo', 'name', 'email', 'phone', 'address'] as const;

function storedBusinessDetails(raw: unknown): Pick<FormPublicSettings, 'businessDetails'> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const record = raw as Record<string, unknown>;
  const known = BUSINESS_DETAIL_KEYS.filter((key) => typeof record[key] === 'boolean');
  if (known.length === 0) return {};
  // A partial stored value fills its gaps from the classic header, as the draft reader does.
  const details = { ...CLASSIC_BUSINESS_DETAILS };
  for (const key of known) details[key] = record[key] as boolean;
  return { businessDetails: details };
}

function storedBranding(raw: unknown): Pick<FormPublicSettings, 'branding'> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  return { branding: readBranding(raw as Record<string, unknown>) };
}

const BUSINESS_DETAIL_LABELS = [
  ['logo', 'Logo'],
  ['name', 'Business name'],
  ['email', 'Email'],
  ['phone', 'Phone'],
  ['address', 'Address'],
] as const;

const PUBLIC_SETTINGS_TEXT_LABELS = [
  ['consentText', 'Consent text'],
  ['submitLabel', 'Submit button label'],
  ['successTitle', 'Success title'],
  ['successMessage', 'Success message'],
  ['redirectUrl', 'Redirect URL'],
] as const;

/**
 * What publishing `next` over `current` would do to the public page, one
 * plain sentence per setting that differs. Empty when they match.
 */
export function publicSettingsChanges(
  current: FormPublicSettings,
  next: FormPublicSettings,
): string[] {
  const changes: string[] = [];
  // Read both through the draft reader, so a version saved before per-detail
  // settings or branding existed compares by what its page actually showed.
  const was = publicSettingsDraftFrom(current);
  const will = publicSettingsDraftFrom(next);
  const anyShown = (draft: PublicSettingsDraft) =>
    Object.values(draft.businessDetails).some(Boolean);
  if (anyShown(was) !== anyShown(will)) {
    changes.push(
      anyShown(will) ? 'Business details will be shown' : 'Business details will be hidden',
    );
  } else {
    for (const [detail, label] of BUSINESS_DETAIL_LABELS) {
      if (was.businessDetails[detail] === will.businessDetails[detail]) continue;
      changes.push(`${label} will be ${will.businessDetails[detail] ? 'shown' : 'hidden'}`);
    }
  }
  if (was.primaryColor !== will.primaryColor) {
    changes.push(
      will.primaryColor === null
        ? 'Brand colour will go back to the company colour'
        : `Brand colour will change to ${will.primaryColor}`,
    );
  }
  if (was.font !== will.font) {
    const label =
      PUBLIC_FORM_FONTS.find((option) => option.value === will.font)?.label ?? will.font;
    changes.push(`Font will change to ${label}`);
  }
  if (was.headerStyle !== will.headerStyle) {
    changes.push(`Header style will change to ${capitalized(will.headerStyle)}`);
  }
  if (was.backgroundColor !== will.backgroundColor) {
    changes.push(
      will.backgroundColor === null
        ? 'Page background will go back to the brand tint'
        : `Page background will change to ${publicFormBackgroundLabel(will.backgroundColor)}`,
    );
  }
  if (was.questionStyle !== will.questionStyle) {
    changes.push(`Question style will change to ${capitalized(will.questionStyle)}`);
  }
  for (const [field, label] of PUBLIC_SETTINGS_TEXT_LABELS) {
    const was = current[field];
    const will = next[field];
    if (was === will) continue;
    changes.push(
      will === null
        ? `${label} will be removed`
        : was === null
          ? `${label} will be added`
          : `${label} will change to “${will}”`,
    );
  }
  return changes;
}

function capitalized(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}
